import { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Spinner from '../../components/common/Spinner';
import SignatureModal from '../../components/settings/SignatureModal';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import * as userSignaturesApi from '../../api/userSignatures';

/**
 * User Signatures Management Component
 * Allows users to create and manage their digital signature
 */
export default function UserSignatures() {
  const [signature, setSignature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showToast } = useUIStore();
  const { user } = useAuthStore();

  useEffect(() => {
    loadSignature();
  }, []);

  const loadSignature = async () => {
    try {
      setLoading(true);
      const response = await userSignaturesApi.getMySignature();
      setSignature(response.signature_blob);
    } catch (error) {
      // No signature exists yet or error occurred
      if (error.response?.status !== 404) {
        console.error('Error loading signature:', error);
      }
      setSignature(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSignature = async (signatureBlob) => {
    try {
      await userSignaturesApi.createOrUpdateSignature(signatureBlob);
      showToast('Signature saved successfully', 'success');
      loadSignature();
    } catch (error) {
      showToast('Failed to save signature', 'error');
      console.error('Error saving signature:', error);
      throw error; // Re-throw to let modal handle it
    }
  };

  const handleDeleteSignature = async () => {
    if (!confirm('Are you sure you want to delete your signature?')) return;

    try {
      await userSignaturesApi.deleteSignature();
      showToast('Signature deleted successfully', 'success');
      setSignature(null);
    } catch (error) {
      showToast('Failed to delete signature', 'error');
      console.error('Error deleting signature:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">My Signature</h3>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage your digital signature for contracts
          </p>
        </div>
      </div>

      {/* Signature Display */}
      {!signature ? (
        <Card>
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No signature created yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Create your signature to use when generating contracts
            </p>
            <div className="mt-6">
              <Button
                variant="primary"
                onClick={() => setIsModalOpen(true)}
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Signature
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="p-6 space-y-4">
            {/* Signature Preview */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Signature
              </label>
              <div className="aspect-video bg-gray-50 rounded-lg border-2 border-gray-200 flex items-center justify-center p-8">
                <img
                  src={signature}
                  alt="Your signature"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            </div>

            {/* User Info */}
            <div className="border-t border-gray-200 pt-4">
              <dl className="grid grid-cols-1 gap-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Signature Owner</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {user?.first_name} {user?.last_name}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">{user?.email}</dd>
                </div>
              </dl>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={() => setIsModalOpen(true)}
                className="flex-1"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Update Signature
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteSignature}
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Help Text */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="text-sm text-blue-800">
          <h4 className="font-semibold mb-2">About Your Signature:</h4>
          <ul className="list-disc list-inside space-y-1">
            <li>Your signature will be automatically applied to contracts you create</li>
            <li>Draw your signature using your mouse, trackpad, or touch screen</li>
            <li>Make sure your signature is clear and matches your official signature</li>
            <li>You can update or delete your signature at any time</li>
          </ul>
        </div>
      </Card>

      {/* Signature Modal */}
      <SignatureModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveSignature}
        existingSignature={signature}
      />
    </div>
  );
}